import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useLang } from "../contexts/LangContext";
import { enableStickyWatcher } from "../utils/stickyWatcher";
import { useUserData } from "../contexts/UserContext";
import { convertInCurrentLang, convertNumberInCurrentLang } from "../utils/misc";
import { fishDatabase } from "../data/fish_db";
import { storageManager } from "../services/storage.manager";

export default function SurveyMode() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { getSurveyedPersons, appData } = useUserData();
  const [isUsingLocalStorage, setIsUsingLocalStorage] = useState(true);

  const dashboardStats = useMemo(() => {
    const persons = getSurveyedPersons() || [];

    let totalDays = 0;
    let totalMeals = 0;
    let totalFishItems = 0;
    let lastLogDate: string | null = null;

    for (const p of persons) {
      const records = p.records || [];
      // Count days with meals OR days explicitly marked as no fish consumed
      for (const day of records) {
        if ((day.meals && day.meals.length > 0) || day.no_fish_consumed) {
          totalDays++;
        }
        
        const meals = day.meals || [];
        totalMeals += meals.length;
        for (const meal of meals) {
          totalFishItems += (meal.fish_items || []).length;
        }

        if (day.date) {
          if (!lastLogDate || day.date > lastLogDate) lastLogDate = day.date;
        }
      }
    }

    return {
      respondents: persons.length,
      totalDays,
      totalMeals,
      totalFishItems,
      lastLogDate,
      pendingSync: !!appData?.pending_sync,
    };
  }, [getSurveyedPersons, appData?.pending_sync]);

  // Get recent logs for quick access
  const recentLogs = useMemo(() => {
    const persons = getSurveyedPersons() || [];
    const logs: Array<{
      personId: string;
      personName: string;
      date: string;
      surveyedOn: string;
      latestMealLogTime: string;
      mealTypes: string[];
    }> = [];

    for (const person of persons) {
      for (const dayRecord of person.records || []) {
        if (dayRecord.meals && dayRecord.meals.length > 0) {
          const mealsWithFish = dayRecord.meals.filter(m => m.fish_items && m.fish_items.length > 0);
          if (mealsWithFish.length > 0) {
            // Get latest meal log time for this day
            const mealTimes = mealsWithFish
              .map(m => m.updated_at || dayRecord.surveyed_on || dayRecord.date)
              .sort();
            const latestMealLogTime = mealTimes[mealTimes.length - 1];

            logs.push({
              personId: person.id,
              personName: person.name || 'Unknown',
              date: dayRecord.date,
              surveyedOn: dayRecord.surveyed_on || dayRecord.date,
              latestMealLogTime,
              mealTypes: mealsWithFish.map(m => m.meal_type),
            });
          }
        }
      }
    }

    // Sort by latest meal log time (most recent first)
    logs.sort((a, b) => {
      return b.latestMealLogTime.localeCompare(a.latestMealLogTime);
    });

    return logs.slice(0, 5);
  }, [getSurveyedPersons]);

  // Advanced stats - calculated lazily to avoid blocking UI
  const [advancedStats, setAdvancedStats] = useState<{
    avgFishPerMeal: number;
    topMealType: string;
    fishVariations: number;
    mostConsumedFish: string;
  } | null>(null);

  // Time stats - calculated lazily
  const [timeStats, setTimeStats] = useState<{
    avgTime: number;
    minTime: number;
    maxTime: number;
    buckets: {
      lessThan1Min: number;
      oneToFiveMin: number;
      fiveToTenMin: number;
      moreThan10Min: number;
    };
    totalSessions: number;
  } | null>(null);

  useEffect(() => {
    // Calculate advanced stats lazily after initial render
    const timer = setTimeout(() => {
      const persons = getSurveyedPersons() || [];
      
      // Track meal type counts and fish counts
      const mealTypeCounts: Record<string, number> = {};
      const fishCounts: Record<string, number> = {};
      const fishVariationsSet = new Set<string>();
      let totalMealsWithFish = 0;
      let totalFishInMeals = 0;

      for (const p of persons) {
        for (const day of p.records || []) {
          for (const meal of day.meals || []) {
            const fishItems = meal.fish_items || [];
            if (fishItems.length > 0) {
              totalMealsWithFish++;
              totalFishInMeals += fishItems.length;
              
              // Count meal type
              mealTypeCounts[meal.meal_type] = (mealTypeCounts[meal.meal_type] || 0) + fishItems.length;
              
              // Count fish species
              for (const item of fishItems) {
                fishVariationsSet.add(item.fish_id);
                fishCounts[item.fish_id] = (fishCounts[item.fish_id] || 0) + 1;
              }
            }
          }
        }
      }

      // Calculate results
      const avgFishPerMeal = totalMealsWithFish > 0 ? totalFishInMeals / totalMealsWithFish : 0;
      
      let topMealType = "—";
      let maxCount = 0;
      for (const [mealType, count] of Object.entries(mealTypeCounts)) {
        if (count > maxCount) {
          maxCount = count;
          topMealType = mealType;
        }
      }

      let mostConsumedFish = "—";
      let maxFishCount = 0;
      for (const [fishId, count] of Object.entries(fishCounts)) {
        if (count > maxFishCount) {
          maxFishCount = count;
          mostConsumedFish = fishId;
        }
      }

      setAdvancedStats({
        avgFishPerMeal,
        topMealType,
        fishVariations: fishVariationsSet.size,
        mostConsumedFish,
      });
    }, 100); // Small delay to not block initial render

    return () => clearTimeout(timer);
  }, [getSurveyedPersons]);

  // Calculate time stats lazily
  useEffect(() => {
    const timer = setTimeout(() => {
      const persons = getSurveyedPersons() || [];
      
      const sessionDurations: number[] = [];
      const buckets = {
        lessThan1Min: 0,
        oneToFiveMin: 0,
        fiveToTenMin: 0,
        moreThan10Min: 0,
      };

      for (const person of persons) {
        for (const dayRecord of person.records || []) {
          for (const meal of dayRecord.meals || []) {
            if (meal.log_sessions && meal.log_sessions.length > 0) {
              for (const session of meal.log_sessions) {
                if (session.started_at && session.completed_at) {
                  const durationMs = new Date(session.completed_at).getTime() - new Date(session.started_at).getTime();
                  const durationMinutes = durationMs / (1000 * 60);
                  
                  sessionDurations.push(durationMinutes);
                  
                  // Bucket the duration
                  if (durationMinutes < 1) {
                    buckets.lessThan1Min++;
                  } else if (durationMinutes < 5) {
                    buckets.oneToFiveMin++;
                  } else if (durationMinutes < 10) {
                    buckets.fiveToTenMin++;
                  } else {
                    buckets.moreThan10Min++;
                  }
                }
              }
            }
          }
        }
      }

      if (sessionDurations.length > 0) {
        const avgTime = sessionDurations.reduce((sum, d) => sum + d, 0) / sessionDurations.length;
        const minTime = Math.min(...sessionDurations);
        const maxTime = Math.max(...sessionDurations);

        setTimeStats({
          avgTime,
          minTime,
          maxTime,
          buckets,
          totalSessions: sessionDurations.length,
        });
      } else {
        setTimeStats(null);
      }
    }, 200); // Delayed after advancedStats

    return () => clearTimeout(timer);
  }, [getSurveyedPersons]);

    // LocalStorage usage calculation
    const [localStorageStats, setLocalStorageStats] = useState({ used: 0, quota: 5 * 1024 * 1024 });

    useEffect(() => {
      // Calculate total bytes used in localStorage
      let total = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        const value = localStorage.getItem(key);
        if (value) {
          total += key.length + value.length;
        }
      }
      setLocalStorageStats({ used: total, quota: 5 * 1024 * 1024 });
    }, []);
  useEffect(() => {
    enableStickyWatcher();
    // Check if localStorage is being used
    const storageType = storageManager.getCurrentStorageType();
    setIsUsingLocalStorage(storageType === 'localStorage');
  }, []);

  useEffect(() => {
    document.title = "Survey Mode - Fish Tracker";
  }, []);

  return (
    <div
      className={
        "page relative flex flex-col grow pt-12 bg-transparent text-center"
      }
    >
      <div className="px-scr pb-20">
        <h1 className="text-4xl font-bold mb-8 text-pink-600">
          {t("surveyMode")}
        </h1>
        <p className="px-4 mb-6 text-base text-gray-700">{t("surveyModeDescription")}</p>

        <div className="space-y-4">
          <button
            onClick={() => navigate("/survey/new-person")}
            className="w-full button-base bg-blue-400 bg-opacity-25 backdrop-blur !rounded-3xl"
          >
            <div className="flex flex-col items-center justify-center text-blue-500 gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white text-blue-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              </div>
              {t("addNewPerson")}
            </div>
          </button>

          <button
            onClick={() => navigate("/survey/person-list")}
            className="w-full button-base bg-pink-400 bg-opacity-25 backdrop-blur !rounded-3xl"
          >
            <div className="flex flex-col items-center justify-center gap-2 text-pink-500">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white text-pink-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"
                  />
                </svg>
              </div>
              {t("viewAllPersons")}
            </div>
          </button>
        </div>

        <div className="mt-6 text-left">
          <div className="bg-purple-300 bg-opacity-20 backdrop-blur-sm rounded-3xl p-4">
            <h2 className="text-lg font-semibold text-[#304050] mb-3">
              {t("surveyDashboard_Title")}
            </h2>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-100 bg-opacity-50 backdrop-blur p-3 rounded-xl">
                <div className="text-sm text-gray-700">{t("surveyDashboard_Respondents")}</div>
                <div className="text-2xl font-black text-blue-900">
                  {convertNumberInCurrentLang(lang, dashboardStats.respondents)}
                </div>
              </div>

              <div className="bg-blue-100 bg-opacity-50 backdrop-blur p-3 rounded-xl">
                <div className="text-sm text-gray-700">{t("surveyDashboard_Days")}</div>
                <div className="text-2xl font-black text-blue-900">
                  {convertNumberInCurrentLang(lang, dashboardStats.totalDays)}
                </div>
              </div>

              <div className="bg-blue-100 bg-opacity-50 backdrop-blur p-3 rounded-xl">
                <div className="text-sm text-gray-700">{t("surveyDashboard_Meals")}</div>
                <div className="text-2xl font-black text-blue-900">
                  {convertNumberInCurrentLang(lang, dashboardStats.totalMeals)}
                </div>
              </div>

              <div className="bg-blue-100 bg-opacity-50 backdrop-blur p-3 rounded-xl">
                <div className="text-sm text-gray-700">{t("surveyDashboard_FishItems")}</div>
                <div className="text-2xl font-black text-blue-900">
                  {convertNumberInCurrentLang(lang, dashboardStats.totalFishItems)}
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between text-sm text-gray-700">
              <span>
                {t("surveyDashboard_LastLog")}: { convertInCurrentLang(lang, dashboardStats.lastLogDate || "—")}
              </span>
              <span>
                {t("surveyDashboard_PendingSync")}: {dashboardStats.pendingSync ? t("yes") : t("no")}
              </span>
            </div>

            {/* Recent Logs Section */}
            {recentLogs.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-300">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-semibold text-[#304050]">
                    {t("surveyDashboard_RecentLogs")}
                  </h3>
                  <button
                    onClick={() => navigate("/survey/all-logs")}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {t("surveyDashboard_ViewAll")} 
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="inline-block ml-2 size-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>

                  </button>
                </div>
                <div className="space-y-2">
                  {recentLogs.map((log, idx) => {
                    // Format log date and time
                    const logDate = new Date(log.latestMealLogTime);
                    const locale = lang === "bn" ? "bn-BD" : "en-US";
                    const dateStr = logDate.toLocaleDateString(locale, {
                      month: 'short',
                      day: 'numeric',
                    });
                    const timeStr = logDate.toLocaleTimeString(locale, {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    });

                    return (
                      <button
                        key={`${log.personId}-${log.date}-${idx}`}
                        onClick={() => navigate(`/log-history/${log.personId}/${log.date}`, { state: { from: '/survey' } })}
                        className="w-full text-left bg-slate-200 bg-opacity-50 backdrop-blur px-3 py-2 rounded-lg hover:bg-slate-200 transition-colors"
                      >
                        <div className="flex items-center justify-between text-base">
                          <span className="font-semibold text-gray-900 truncate flex-1 mr-2">
                            {log.personName}
                          </span>
                          <span className="text-gray-600 font-semibold text-[14px]">
                            {convertInCurrentLang(lang, log.date || '')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <div className="text-[13px] text-gray-700">
                            {log.mealTypes.map((mealType, i) => (
                              <span key={i} className="capitalize">
                                {t(`mealType_${mealType}` as any)}
                                {i < log.mealTypes.length - 1 ? ', ' : ''}
                              </span>
                            ))}
                          </div>
                          <span className="text-[12px] text-gray-500">
                            {dateStr} {timeStr}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Advanced Stats Section */}
            {advancedStats ? (
              <div className="mt-4 pt-4 border-t border-gray-300">
                <h3 className="text-base font-semibold text-[#304050] mb-2">
                  {t("surveyDashboard_AdvancedStats")}
                </h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-green-200 bg-opacity-40 backdrop-blur p-2 rounded-lg">
                    <div className="text-gray-700">{t("surveyDashboard_AvgFishPerMeal")}</div>
                    <div className="text-lg font-bold text-green-900">
                      {convertNumberInCurrentLang(lang, parseFloat(advancedStats.avgFishPerMeal.toFixed(1)))}
                    </div>
                  </div>
                  <div className="bg-green-200 bg-opacity-40 backdrop-blur p-2 rounded-lg">
                    <div className="text-gray-700">{t("surveyDashboard_TopMealType")}</div>
                    <div className="text-base font-bold text-green-900 capitalize">
                      {advancedStats.topMealType === "—" 
                        ? "—" 
                        : t("mealType_" + advancedStats.topMealType.toLowerCase() as any) || advancedStats.topMealType}
                    </div>
                  </div>
                  <div className="bg-green-200 bg-opacity-40 backdrop-blur p-2 rounded-lg">
                    <div className="text-gray-700">{t("surveyDashboard_FishVariations")}</div>
                    <div className="text-lg font-bold text-green-900">
                      {convertNumberInCurrentLang(lang, advancedStats.fishVariations)}
                    </div>
                  </div>
                  <div className="bg-green-200 bg-opacity-40 backdrop-blur p-2 rounded-lg">
                    <div className="text-gray-700">{t("surveyDashboard_MostConsumedFish")}</div>
                    <div className="text-base font-bold text-green-900 truncate">
                      {advancedStats.mostConsumedFish === "—" 
                        ? "—" 
                        : (() => {
                            const fish = fishDatabase.find(f => f.id === advancedStats.mostConsumedFish);
                            return fish ? (lang === "bn" ? fish.local_name : fish.english_name) : advancedStats.mostConsumedFish;
                          })()}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 pt-4 border-t border-gray-300 text-center text-sm text-gray-500">
                {t("loading")}...
              </div>
            )}

            {/* Time Stats Section */}
            {timeStats ? (
              <div className="mt-4 pt-4 border-t border-gray-300">
                <h3 className="text-base font-semibold text-[#304050] mb-2">
                  {t("surveyDashboard_TimeStats")}
                </h3>
                <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                  <div className="bg-indigo-200 bg-opacity-40 backdrop-blur p-2 rounded-lg">
                    <div className="text-gray-700">{t("surveyDashboard_AvgTime")}</div>
                    <div className="text-base font-bold text-indigo-900">
                      {(() => {
                        const minutes = Math.floor(timeStats.avgTime);
                        const seconds = Math.floor((timeStats.avgTime % 1) * 60);
                        return `${convertNumberInCurrentLang(lang, minutes)}:${seconds < 10 ? '0' : ''}${convertNumberInCurrentLang(lang, seconds)}`;
                      })()}
                    </div>
                  </div>
                  <div className="bg-indigo-200 bg-opacity-40 backdrop-blur p-2 rounded-lg">
                    <div className="text-gray-700">{t("surveyDashboard_MinTime")}</div>
                    <div className="text-base font-bold text-indigo-900">
                      {(() => {
                        const minutes = Math.floor(timeStats.minTime);
                        const seconds = Math.floor((timeStats.minTime % 1) * 60);
                        return `${convertNumberInCurrentLang(lang, minutes)}:${seconds < 10 ? '0' : ''}${convertNumberInCurrentLang(lang, seconds)}`;
                      })()}
                    </div>
                  </div>
                  <div className="bg-indigo-200 bg-opacity-40 backdrop-blur p-2 rounded-lg">
                    <div className="text-gray-700">{t("surveyDashboard_MaxTime")}</div>
                    <div className="text-base font-bold text-indigo-900">
                      {(() => {
                        const minutes = Math.floor(timeStats.maxTime);
                        const seconds = Math.floor((timeStats.maxTime % 1) * 60);
                        return `${convertNumberInCurrentLang(lang, minutes)}:${seconds < 10 ? '0' : ''}${convertNumberInCurrentLang(lang, seconds)}`;
                      })()}
                    </div>
                  </div>
                </div>
                
                {/* Bar Chart */}
                <div className="space-y-2">
                  <div className="text-xs text-gray-600 font-medium mb-1">
                    {t("surveyDashboard_SessionDistribution")} ({convertNumberInCurrentLang(lang, timeStats.totalSessions)} {t("mealLog_Sessions")})
                  </div>
                  {[
                    { key: 'lessThan1Min', label: t("surveyDashboard_LessThan1Min"), count: timeStats.buckets.lessThan1Min, color: 'bg-green-500' },
                    { key: 'oneToFiveMin', label: t("surveyDashboard_1To5Min"), count: timeStats.buckets.oneToFiveMin, color: 'bg-yellow-500' },
                    { key: 'fiveToTenMin', label: t("surveyDashboard_5To10Min"), count: timeStats.buckets.fiveToTenMin, color: 'bg-orange-500' },
                    { key: 'moreThan10Min', label: t("surveyDashboard_MoreThan10Min"), count: timeStats.buckets.moreThan10Min, color: 'bg-red-500' },
                  ].map((bucket) => {
                    const percentage = timeStats.totalSessions > 0 ? (bucket.count / timeStats.totalSessions) * 100 : 0;
                    return (
                      <div key={bucket.key} className="flex items-center gap-y-2">
                        <div className="text-xs flex items-center justify-end bg-pink-200 rounded-l-full h-5 text-gray-700 w-20 px-2 flex-shrink-0 text-right">
                          {bucket.label}
                        </div>
                        <div className="flex-1 bg-gray-200 rounded-r-full h-5 overflow-hidden relative">
                          <div
                            className={`${bucket.color} h-5 rounded-r-full transition-all duration-500`}
                            style={{ width: `${percentage}%` }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-800">
                            {convertNumberInCurrentLang(lang, bucket.count)} ({convertNumberInCurrentLang(lang, Math.round(percentage))}%)
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : timeStats === null && advancedStats ? (
              <div className="mt-4 pt-4 border-t border-gray-300 text-center text-sm text-gray-500">
                {t("loading")}...
              </div>
            ) : null}

              {isUsingLocalStorage && (
                <div className="border-t pt-2 mt-3 flex flex-col gap-1 text-xs text-gray-600">
                  <div>
                    <strong>Local Storage Used:</strong> {Math.round(localStorageStats.used / 1024)} KB / 5120 KB
                  </div>
                  <div className="w-full bg-purple-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-pink-400 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(100, (localStorageStats.used / localStorageStats.quota) * 100)}%` }}
                    />
                  </div>
                  <div>
                    <strong>Approx. Free:</strong> {Math.max(0, Math.round((localStorageStats.quota - localStorageStats.used) / 1024))} KB
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>

      <div className="bottom-nav-bar flex justify-center items-center w-full sticky bottom-0 px-6 py-4 bg-white bg-opacity-75 backdrop-blur z-10 border-t">
        <button
          className="button-xs bg-gray-500 text-gray-800"
          onClick={() => navigate("/user")}
        >
          <span>{t("userMode")}</span>
        </button>
      </div>
    </div>
  );
}
